import { CopyOutlined } from '@ant-design/icons'
import { Empty, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  copyTicketId,
  TicketTitleBtn,
} from 'features/libra/dashboard/copyTicketId'
import type { FC } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'

import {
  type FieldComparisonDetail,
  formatAcceptRate,
  type NormalizedLlmFieldComparison,
  ticketAcceptRate,
  type TicketLlmStats,
} from './computeTicketLlmStats'

const COMPARISON_TAG: Record<
  NormalizedLlmFieldComparison,
  { color: string; label: string }
> = {
  accepted: { color: 'green', label: 'Accepted' },
  edited: { color: 'gold', label: 'Edited' },
  rejected: { color: 'red', label: 'Rejected' },
  bothMissing: { color: 'default', label: 'No suggestion' },
}

const innerColumns: ColumnsType<FieldComparisonDetail> = [
  {
    title: 'Field',
    dataIndex: 'fieldName',
    key: 'fieldName',
    width: 240,
    ellipsis: true,
    render: (name: string, record) => (
      <FieldCell>
        <span>{name}</span>
        {name !== record.fieldId && (
          <FieldIdSubtext>{record.fieldId}</FieldIdSubtext>
        )}
      </FieldCell>
    ),
  },
  {
    title: 'Status',
    dataIndex: 'comparison',
    key: 'comparison',
    width: 130,
    render: (v: NormalizedLlmFieldComparison) => {
      const cfg = COMPARISON_TAG[v]
      return <Tag color={cfg.color}>{cfg.label}</Tag>
    },
  },
  {
    title: 'User value',
    dataIndex: 'userText',
    key: 'userText',
    width: '50%',
    render: (text: string) => <ValueBlock>{text}</ValueBlock>,
  },
  {
    title: 'LLM value',
    dataIndex: 'llmText',
    key: 'llmText',
    width: '50%',
    render: (text: string) => <ValueBlock>{text}</ValueBlock>,
  },
]

const expandedRowRender = (record: TicketLlmStats) => (
  <ExpandedTableWrapper>
    <Table<FieldComparisonDetail>
      size='small'
      rowKey='fieldId'
      columns={innerColumns}
      dataSource={record.fields}
      pagination={false}
      tableLayout='fixed'
      locale={{ emptyText: 'No AI-generated fields on this ticket.' }}
    />
  </ExpandedTableWrapper>
)

export const PerTicketTable: FC<{ tickets: TicketLlmStats[] }> = ({
  tickets,
}) => {
  const columns = useMemo<ColumnsType<TicketLlmStats>>(
    () => [
      {
        title: 'Ticket',
        dataIndex: 'ticketTitle',
        key: 'ticketTitle',
        ellipsis: true,
        render: (title: string, record) => (
          <Tooltip title={`Copy ID: ${record.ticketId}`} placement='topLeft'>
            <TicketTitleBtn onClick={() => copyTicketId(record.ticketId)}>
              {title}
              <CopyOutlined />
            </TicketTitleBtn>
          </Tooltip>
        ),
      },
      {
        title: 'Site',
        dataIndex: 'site',
        key: 'site',
        width: 120,
        sorter: (a, b) => (a.site ?? '').localeCompare(b.site ?? ''),
        render: (site: string | undefined) => site ?? '—',
      },
      {
        title: 'Accepted',
        dataIndex: 'accepted',
        key: 'accepted',
        align: 'center',
        width: 100,
        sorter: (a, b) => a.accepted - b.accepted,
        render: (n: number) => <AcceptedCell>{n}</AcceptedCell>,
      },
      {
        title: 'Edited',
        dataIndex: 'edited',
        key: 'edited',
        align: 'center',
        width: 100,
        sorter: (a, b) => a.edited - b.edited,
        render: (n: number) => <EditedCell>{n}</EditedCell>,
      },
      {
        title: 'Rejected',
        dataIndex: 'rejected',
        key: 'rejected',
        align: 'center',
        width: 100,
        sorter: (a, b) => a.rejected - b.rejected,
        render: (n: number) => <RejectedCell>{n}</RejectedCell>,
      },
      {
        title: 'Accept rate',
        key: 'acceptRate',
        align: 'right',
        width: 110,
        // Sort is available but no `defaultSortOrder`: the initial order
        // honours the feed, which the compute module delivers as
        // most-recently-updated first.
        sorter: (a, b) => ticketAcceptRate(a) - ticketAcceptRate(b),
        render: (_: unknown, record) =>
          formatAcceptRate(ticketAcceptRate(record), record.totalFieldsWithLlm),
      },
    ],
    []
  )

  if (tickets.length === 0) {
    return (
      <Empty description='No tickets with AI-generated fields in this range' />
    )
  }

  return (
    <FlushExpansionWrapper>
      <Table<TicketLlmStats>
        size='small'
        rowKey='ticketId'
        columns={columns}
        dataSource={tickets}
        pagination={
          tickets.length > 10 ? { pageSize: 10, size: 'small' } : false
        }
        expandable={{
          expandedRowRender,
          rowExpandable: (t) => t.fields.length > 0,
        }}
      />
    </FlushExpansionWrapper>
  )
}

const statCellBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
`

const AcceptedCell = styled.span`
  ${statCellBase}
  color: #52c41a;
`

const EditedCell = styled.span`
  ${statCellBase}
  color: #faad14;
`

const RejectedCell = styled.span`
  ${statCellBase}
  color: #ff4d4f;
`

/**
 * Zero the default padding antd applies to the expanded-row cell so our
 * inner table sits flush with the outer table edges — more robust than
 * a negative-margin hack on the inner wrapper, which had to track antd's
 * padding defaults.
 */
const FlushExpansionWrapper = styled.div`
  .ant-table-expanded-row > .ant-table-cell {
    padding: 0;
  }
`

const ExpandedTableWrapper = styled.div`
  padding: 0.25rem 0;
  background: ${({ theme }) => theme.colors.gray[800]};
  border-top: 1px solid ${({ theme }) => theme.colors.gray[700]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray[700]};
`

const FieldCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  line-height: 1.3;
`

const FieldIdSubtext = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const ValueBlock = styled.pre`
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.8125rem;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow: auto;
`
